<template>
  <div class="language-dropdown-wrap" :class="{ 'language-dropdown-wrap--no-offset': noOffset }">
    <v-menu offset-y left :nudge-left="menuNudgeLeft" content-class="language-dropdown-menu">
    <template v-slot:activator="{ on, attrs }">
      <v-btn
        small
        text
        :dark="!light"
        v-bind="attrs"
        v-on="on"
        class="language-dropdown-btn"
        :class="{ 'language-dropdown-btn--light': light }"
        min-width="100"
      >
        <span class="locale-flag locale-flag--button" aria-hidden="true">{{ currentLocaleFlag }}</span>
        <span class="locale-label">{{ currentLocaleLabel }}</span>
        <v-icon small class="ml-1">$vuetify.icons.caret_down</v-icon>
      </v-btn>
    </template>
    <v-list dense class="language-dropdown-list">
      <v-list-item
        v-for="loc in locales"
        :key="loc.code"
        @click="setLocale(loc.code)"
        :class="{ 'v-list-item--active': locale === loc.code }"
      >
        <span class="locale-flag" aria-hidden="true">{{ loc.flag }}</span>
        <v-list-item-title>{{ loc.label }}</v-list-item-title>
      </v-list-item>
    </v-list>
  </v-menu>
  </div>
</template>

<script lang="ts">
import Vue from 'vue'

const LOCALE_STORAGE_KEY = 'i911-locale'

const LOCALES = [
  { code: 'en', label: 'English', flag: '🇺🇸' },
  { code: 'es', label: 'Español', flag: '🇪🇸' },
  { code: 'fr', label: 'Français', flag: '🇫🇷' },
  { code: 'de', label: 'Deutsch', flag: '🇩🇪' },
  { code: 'it', label: 'Italiano', flag: '🇮🇹' },
  { code: 'pt', label: 'Português', flag: '🇵🇹' },
]

export default Vue.extend({
  props: {
    light: { type: Boolean, default: false },
    noOffset: { type: Boolean, default: false },
    menuNudgeLeft: { type: Number, default: 8 },
  },
  data() {
    return {
      locales: LOCALES,
    }
  },
  computed: {
    locale(): string {
      return (this as any).$i18n?.locale || 'en'
    },
    currentLocaleLabel(): string {
      const loc = LOCALES.find((l) => l.code === this.locale)
      return loc ? loc.label : this.locale.toUpperCase()
    },
    currentLocaleFlag(): string {
      const loc = LOCALES.find((l) => l.code === this.locale)
      return loc ? loc.flag : '🌐'
    },
  },
  methods: {
    setLocale(code: string) {
      (this as any).$i18n.locale = code
      try {
        localStorage.setItem(LOCALE_STORAGE_KEY, code)
      } catch {
        // localStorage may be unavailable
      }
    },
  },
  mounted() {
    try {
      const saved = localStorage.getItem(LOCALE_STORAGE_KEY)
      if (saved && LOCALES.some((l) => l.code === saved)) {
        (this as any).$i18n.locale = saved
      }
    } catch {
      // localStorage may be unavailable
    }
  },
})
</script>

<style lang="scss" scoped>
.language-dropdown-wrap {
  margin-left: 2% !important;

  &--no-offset {
    margin-left: 0 !important;
  }
}

.language-dropdown-btn {
  text-transform: none;
  letter-spacing: 0.3px;
  .locale-label {
    font-size: 0.875rem;
  }
  &--light .locale-label {
    color: #1f2937 !important;
  }
  &--light .v-icon {
    color: #1f2937 !important;
  }
  &:hover,
  &:focus {
    background: transparent !important;
    opacity: 1 !important;
  }
  &:hover::before,
  &:focus::before {
    opacity: 0 !important;
  }
}

:deep(.language-dropdown-menu) {
  margin-top: 6px;
  border-radius: 8px;
  box-shadow: 0 4px 12px rgba(0, 0, 0, 0.15);
  min-width: 160px;
  color: #1f2937;

  .v-list-item {
    cursor: pointer;
    color: #1f2937 !important;
  }
  .v-list-item__title {
    color: #1f2937 !important;
  }
  .v-list-item:hover,
  .v-list-item:focus {
    background: transparent !important;
  }
  .v-list-item:hover::before,
  .v-list-item:focus::before {
    opacity: 0 !important;
  }
}

.language-dropdown-list {
  padding: 4px;
}

.locale-flag {
  font-size: 1rem;
  line-height: 1;
  margin-right: 10px;
  flex-shrink: 0;
}

.locale-flag--button {
  margin-right: 6px;
}

:deep(.language-dropdown-list .v-list-item__content) {
  display: flex;
  align-items: center;
}
</style>
